import { ref, computed } from 'vue';
import deliveryFeeService, { type DeliveryFeeCalculationRequest, type DeliveryFeeCalculationResult } from '@/services/deliveryFeeService';

export function useDeliveryFee() {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastCalculation = ref<DeliveryFeeCalculationResult | null>(null);
  
  // Request deduplication mechanism
  let currentRequestId = 0;
  const currentRequestController = ref<AbortController | null>(null);
  const pendingRequests = new Map<string, Promise<number>>();

  /**
   * Calculate delivery fee for an order with request deduplication
   */
  const calculateDeliveryFee = async (request: DeliveryFeeCalculationRequest): Promise<number> => {
    // Create a unique key for this request
    const requestKey = JSON.stringify({
      order_total: request.order_total,
      order_date: request.order_date,
      customer_address: request.customer_address,
      has_promotion: request.has_promotion
    });

    // Check if there's already a pending request with the same parameters
    if (pendingRequests.has(requestKey)) {
      return pendingRequests.get(requestKey)!;
    }

    // Generate unique request ID
    const requestId = ++currentRequestId;
    
    // Cancel previous request if still pending
    if (currentRequestController.value) {
      currentRequestController.value.abort();
    }
    
    // Create new abort controller for this request
    const controller = new AbortController();
    currentRequestController.value = controller;
    
    loading.value = true;
    error.value = null;

    // Create the request promise
    const requestPromise = (async () => {
      try {
        const result = await deliveryFeeService.calculateDeliveryFee(request);
        
        // Check if request was cancelled during API call
        if (controller.signal.aborted) {
          return lastCalculation.value?.delivery_fee || 0;
        }
        
        // Only update state if this is the latest request
        if (requestId === currentRequestId) {
          lastCalculation.value = result;
          const deliveryFeeAmount = result.delivery_fee;
          return deliveryFeeAmount;
        } else {
          return lastCalculation.value?.delivery_fee || 0;
        }
      } catch (err: any) {
        // Only handle error if this is the latest request
        if (requestId === currentRequestId) {
          if (err.name === 'AbortError') {
            return lastCalculation.value?.delivery_fee || 0;
          }
          
          // Handle other errors
          error.value = err.response?.data?.error?.message || err.message || 'Failed to calculate delivery fee';
          return 0; // Return 0 on error to avoid blocking order creation
        } else {
          return lastCalculation.value?.delivery_fee || 0;
        }
      } finally {
        // Only clear loading state if this is the latest request
        if (requestId === currentRequestId) {
          loading.value = false;
          currentRequestController.value = null;
        }
        // Remove from pending requests when complete
        pendingRequests.delete(requestKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  };

  /**
   * Calculate delivery fee for a delivery order based on order data
   */
  const calculateDeliveryFeeForOrder = async (
    orderType: string,
    subtotal: number,
    customerData?: {
      address?: string;
      neighborhood?: string;
      city?: string;
      postal_code?: string;
    },
    hasPromotion?: boolean
  ): Promise<number> => {
    // Only calculate for delivery orders
    if (orderType !== 'Delivery') {
      return 0;
    }

    const request: DeliveryFeeCalculationRequest = {
      order_total: subtotal,
      customer_address: customerData,
      order_date: new Date().toISOString(),
      has_promotion: hasPromotion
    };

    try {
      const result = await calculateDeliveryFee(request);
      return result;
    } catch (error) {
      console.error('❌ [COMPOSABLE] Error in calculateDeliveryFee:', error);
      return 0;
    }
  };

  /**
   * Get the applied rules from the last calculation
   */
  const getAppliedRules = computed(() => {
    return lastCalculation.value?.applied_rules || [];
  });

  /**
   * Get the calculation result details
   */
  const getCalculationDetails = computed(() => {
    return lastCalculation.value;
  });

  /**
   * Reset the calculation state and cancel any pending requests
   */
  const reset = () => {
    // Cancel any pending request
    if (currentRequestController.value) {
      currentRequestController.value.abort();
      currentRequestController.value = null;
    }
    
    // Clear all pending requests
    pendingRequests.clear();
    
    // Reset state
    loading.value = false;
    error.value = null;
    lastCalculation.value = null;
    currentRequestId = 0;
  };

  return {
    loading,
    error,
    lastCalculation,
    calculateDeliveryFee,
    calculateDeliveryFeeForOrder,
    getAppliedRules,
    getCalculationDetails,
    reset
  };
}

export default useDeliveryFee;
